import { useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { useAppDispatch } from '../../../../store/hooks';
import { differenceInCalendarDays } from 'date-fns';

import { useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange } from '@zg-rentals/ts-rent-payment-client-react-query';

import { formatDate } from '@common/paymentsUtils/dateUtils';
import { ignoreRentAmountChange } from '@renter-hub/actions/onboarding';
import FaqComponent from '@common/components/ConstellationFaqComponent/index';
import pathsManifest from '@renter-hub/pathsManifest';
import type { ErrorInterface } from '@common/types';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import {
  DEFAULT_ERROR,
  RENT_AMOUNT_CHANGE_ACCEPTED,
  REVIEW_RENT_AMOUNT_CHANGE,
} from '@renter-hub/constants/notifications';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import { Box, Button, ButtonGroup, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import Page from '@renter-hub/components/RenterPage';
import NotificationContainer from '@common/components/NotificationContainer';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import userPersonas from '@common/constants/userPersonas';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import { logError } from '@zg-rentals/log-error';
import { coerceToDate } from '@common/paymentsUtils/dateUtils';

interface ConfirmNewRentPageProps {
  ignoreRentAmountChangeFromStorybook?: () => void;
  handleSubmitFromStorybook?: () => void;
}

const ConfirmNewRentPage = ({
  ignoreRentAmountChangeFromStorybook,
  handleSubmitFromStorybook,
}: ConfirmNewRentPageProps) => {
  const dispatch = useAppDispatch();
  const { rentTerms, landlordProfile, propertyId } = useSelector((state) => ({
    // @ts-expect-error
    rentTerms: state.payments.renter.dashboard?.rentTerms,
    // @ts-expect-error
    landlordProfile: state.payments.renter.dashboard?.landlordProfile,
    // @ts-expect-error
    propertyId: state.payments.renter.dashboard?.propertyId,
  }));

  const { mutateAsync: requestAcceptRentAmountChange } =
    useAcceptRentAmountChangeWebApiHandler_acceptRentAmountChange();

  const { rentAmountChangeRequest } = rentTerms;
  const { newAmount, effectiveDueDate } = rentAmountChangeRequest;
  const daysLeftToAccept = effectiveDueDate
    ? differenceInCalendarDays(coerceToDate(effectiveDueDate)!, new Date())
    : undefined;
  const daysLeftToAcceptSuffix = daysLeftToAccept === 1 ? 'day' : 'days';

  const handleSubmit = async () => {
    if (handleSubmitFromStorybook) {
      return handleSubmitFromStorybook();
    }

    try {
      await dispatch(closeNotification());

      await requestAcceptRentAmountChange({
        postData: { newAmount },
      });

      // Request the latest renter status
      const renterStatusResponse = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Navigate back to the dashboard
      await dispatch(push(pathsManifest.baseUrl));

      // Show a success banner
      await dispatch(
        showPageNotification(RENT_AMOUNT_CHANGE_ACCEPTED, {
          newAmountFormatted: centsToDollars(newAmount),
          effectiveDueDateFormatted: formatDate(effectiveDueDate, 'MMMM do'),
        }),
      );
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      dispatch(showPageNotification(DEFAULT_ERROR));
    }
  };

  const handleIgnoreRentAmountChange = async () => {
    if (ignoreRentAmountChangeFromStorybook) {
      return ignoreRentAmountChangeFromStorybook();
    }

    await dispatch(ignoreRentAmountChange());
    await dispatch(showPageNotification(REVIEW_RENT_AMOUNT_CHANGE, { landlordFirstName: landlordProfile.firstName }));
  };

  return (
    <Page>
      <HelmetContainer {...SeoConstants.confirmRentAmountChangePage} />
      <Page.MainSection>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
          <NotificationContainer persona={userPersonas.RENTER} />
          <Heading level={1} textStyle="heading-sm">
            {landlordProfile.firstName} has updated your rent to {centsToDollars(newAmount)}/mo
          </Heading>
          <Box>
            <Paragraph textStyle="body-bold">When will this change go into effect?</Paragraph>
            <Paragraph>
              On <strong>{formatDate(effectiveDueDate, 'MMMM do')}</strong> if you agree to the new rent amount
              within&nbsp;
              <strong>
                {daysLeftToAccept} {daysLeftToAcceptSuffix}
              </strong>
              . When you agree, any recurring payments you’ve scheduled for that date or later will automatically update
              to the new amount.
            </Paragraph>
          </Box>
          <Box>
            <Paragraph textStyle="body-bold">What happens if I don’t agree to this change?</Paragraph>
            <Paragraph>
              You current payments will continue as usual until you agree to the new amount set by your landlord.
            </Paragraph>
          </Box>
          <ButtonGroup aria-label="New rent amount actions" role="group">
            <DebouncedButton emphasis="filled" fluid={true} size="lg" onClick={handleSubmit}>
              Agree to new monthly rent
            </DebouncedButton>
            <Button size="lg" fluid={true} onClick={() => (location.href = `mailto:${landlordProfile.email}`)}>
              Contact {landlordProfile.firstName}
            </Button>
          </ButtonGroup>
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <TextButton onClick={handleIgnoreRentAmountChange}>Go to your dashboard</TextButton>
          </Box>
        </Box>
      </Page.MainSection>
      <Page.RightSection>
        <FaqComponent />
      </Page.RightSection>
      <Page.BottomSection>
        <FaqComponent />
      </Page.BottomSection>
    </Page>
  );
};

export default ConfirmNewRentPage;
